import { Component, computed, ElementRef, HostListener, inject, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { AppMenu } from './app.menu';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule, RoutesRecognized } from '@angular/router';
import { CommonModule } from '@angular/common';
import { LayoutService } from '@/layout/service/layout.service';
import { SidebarService } from '@/core/services/sidebar-state.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import { SIDEBAR_MENU_ITEMS } from '@/shared/constants/system.constants';
import { ThemeRuntimeService } from '@/theme/theme-runtime.service';
@Component({
    selector: '[app-sidebar]',
    standalone: true,
    imports: [AppMenu, ButtonModule, RouterModule, CommonModule, TooltipModule],
    template: `
        <div class="sidebar-header" [class.collapsed]="isCollapsed">
            <!-- Logo de la aplicación. Vive aquí y no en el topbar (como en
                 Freya/Diamond/Poseidon); el topbar cedió ese ancho al breadcrumb. -->
            <a [routerLink]="['/']" class="sidebar-logo" pTooltip="Ir al inicio" tooltipPosition="right" [showDelay]="500">
                @switch (logoVariant()) {
                    @case ('isotipo') {
                        <span class="sidebar-isotipo" role="img" aria-label="SyM70"></span>
                    }
                    @case ('plate') {
                        <img class="sidebar-plate" src="/images/brand/app-icon-512.png" alt="SyM70" />
                    }
                    @default {
                        <!-- Logotipo: colapsado no cabe el wordmark, cae al isotipo. -->
                        @if (isCollapsed) {
                            <span class="sidebar-isotipo" role="img" aria-label="SyM70"></span>
                        } @else {
                            <img [src]="'/images/' + logo() + '.svg'" alt="SyM70" />
                        }
                    }
                }
            </a>
        </div>

        <div class="sidebar-content" [class.collapsed]="isCollapsed">
            <div  app-menu [menuItems]="items" [collapsed]="isCollapsed" class="layout-menu-container" [class.collapsed]="isCollapsed"></div>
        </div>
    `,
    host: {
        class: 'layout-sidebar',
        '[class.collapsed]': 'isCollapsed'
    }
})
export class AppSidebar implements OnInit, OnDestroy {
    el = inject(ElementRef);
    router = inject(Router);
    layoutService = inject(LayoutService);
    activateRoute = inject(ActivatedRoute);
    parentRoute: 'tender' | 'project-management' | 'app' | 'testing' | 'reports' | null = null;
    themeRuntime = inject(ThemeRuntimeService);
    private sidebarService = inject(SidebarService);
    private destroy$ = new Subject<void>();

    @ViewChild(AppMenu) appMenu!: AppMenu;

    isCollapsed = false;
    timeout!: any;

    /**
     * La variante del logo depende del cromo del SIDEBAR, no del tema de la
     * app: con el esquema Navy (o Degradado/Primary) el sidebar es oscuro
     * incluso en modo claro, y ahí el logo normal —tinta navy sobre navy— se
     * vuelve invisible. Ver SIDEBAR_SCHEMES.darkChrome en theme-catalog.ts.
     */
    logo = computed(() => (this.themeRuntime.sidebarHasDarkChrome() || this.layoutService.isDarkTheme() ? 'brand/logo-horizontal-negativo' : 'brand/logo-horizontal'));

    /** Marca elegida en el configurador: logotipo, isotipo o placa. */
    logoVariant = computed(() => this.themeRuntime.logoVariant());

    items: MenuItem[] = [];

    ngOnInit() {
        const url = this.router.url;
        this.parentRoute = this.getParentRoute(url);

        const syncProject = () => {
            const projectId = this.getRouteParam(this.router.routerState.snapshot.root, 'projectId');
            projectId && this.sidebarService.setMenuItemsForProjectId(Number(projectId));
        };
        // También la URL de entrada: abrir un enlace directo a un proyecto no emite un NavigationEnd
        // que este componente alcance a escuchar (quedaba sin menú del proyecto ni tope de fechas).
        syncProject();
        this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(syncProject);

        this.sidebarService.isCollapsed$
            .pipe(takeUntil(this.destroy$))
            .subscribe(collapsed => {
                this.isCollapsed = collapsed;
            });

        this.sidebarService.menuItems$
            .pipe(takeUntil(this.destroy$))
            .subscribe(items => {
                this.items = items;
            });
    }

    private getRouteParam(snapshot: ActivatedRouteSnapshot, param: string): string | null {
        if (snapshot.paramMap.has(param)) {
        return snapshot.paramMap.get(param);
        }
        for (const child of snapshot.children) {
        const value = this.getRouteParam(child, param);
        if (value) return value;
        }
        return null;
    }

    getMenuItems(module: 'tender' | 'project-management' | 'app' | 'testing'): MenuItem[] {
        return SIDEBAR_MENU_ITEMS[module] || [];
    }

    getParentRoute(url: string) {
        const segments = url.split('/');
        if (segments.length > 1) {
            return segments[1] as 'tender' | 'project-management' | 'app' | 'testing' | 'reports';
        }

        return null;
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onAnchorToggle() {
        this.layoutService.layoutState.update((val) => ({ ...val, anchored: !val.anchored }));
    }

    @HostListener('mouseenter', ['$event'])
    onMouseEnter(event: MouseEvent) {
        if (!this.layoutService.layoutState().anchored) {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
            this.layoutService.layoutState.update((val) => ({ ...val, sidebarActive: true }));
        }
    }

    @HostListener('mouseleave', ['$event'])
    onMouseLeave(event: MouseEvent) {
        if (!this.layoutService.layoutState().anchored) {
            if (!this.timeout) {
                this.timeout = setTimeout(() => {
                    this.layoutService.layoutState.update((val) => ({ ...val, sidebarActive: false }));
                    this.timeout = null;
                }, 300);
            }
        }
    }
}